import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'motion/react';
import { Pause, Play } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.js';
import { BANNER_QUERY_KEYS, fetchActiveBanners } from './api.js';
import { selectBannersToShow } from './select.js';

const ROTATE_MS = 6000;
const SWIPE_PX = 60;
const SWIPE_VELOCITY = 500;

/**
 * The CMS banners (BO /content → Banners) at the top of the home page: a
 * carousel of every banner eligible for this visitor, highest priority first,
 * art then copy, with pill navigation, swipe, and autoplay that a visible
 * button can pause (WCAG 2.2.2). Autoplay also holds while the carousel is
 * hovered or focused, and never runs under reduced motion.
 *
 * Self-hiding: a brand with no banner configured gets no empty frame, so the
 * shortcut grid below simply moves up.
 */
export function HeroBanner() {
  const { t } = useTranslation('lobby');
  const authed = Boolean(useAuthStore((s) => s.accessToken));
  const reduceMotion = useReducedMotion();

  const { data } = useQuery({
    queryKey: BANNER_QUERY_KEYS.active,
    queryFn: fetchActiveBanners,
    staleTime: 5 * 60_000,
  });
  const banners = useMemo(() => selectBannersToShow(data ?? [], { authed }), [data, authed]);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [held, setHeld] = useState(false);
  const dragged = useRef(false);

  const count = banners.length;
  const active = count > 0 ? index % count : 0;
  const rotating = count > 1 && !paused && !held && !reduceMotion;

  // Keyed on `active` so any navigation (pill, swipe, or the tick itself)
  // restarts the full interval instead of cutting the next slide short.
  useEffect(() => {
    if (!rotating) return;
    const id = setTimeout(() => setIndex((i) => (i + 1) % count), ROTATE_MS);
    return () => clearTimeout(id);
  }, [rotating, active, count]);

  const current = banners[active];
  if (!current) return null;

  const go = (next: number) => setIndex(((next % count) + count) % count);

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -SWIPE_PX || info.velocity.x < -SWIPE_VELOCITY) go(active + 1);
    else if (info.offset.x > SWIPE_PX || info.velocity.x > SWIPE_VELOCITY) go(active - 1);
  };

  const slideMotion = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: 24 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -24 },
        transition: { duration: 0.25 },
      };

  // Alt is the generic label, NOT `current.name`: that is the operator's
  // internal campaign name, which the backoffice help copy promises players
  // never see. The art keeps its own shape — often a cut-out PNG over the
  // animated page background — so it is never cropped or framed.
  const image = (
    <img
      src={current.imageUrl}
      alt={t('banners.imageAlt')}
      draggable={false}
      className="mx-auto block h-auto w-auto max-h-[60vh] max-w-full select-none object-contain"
    />
  );

  const hasCopy = Boolean(current.title || current.subtitle || current.description);
  const multiple = count > 1;

  return (
    <section
      aria-roledescription="carousel"
      aria-label={t('banners.carouselLabel')}
      onPointerEnter={(e) => e.pointerType === 'mouse' && setHeld(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setHeld(false)}
      className="flex flex-col gap-4 text-left"
    >
      {/* Controls come first in reading and tab order (WAI-ARIA carousel), and
          sit visually between the art and the title, where the design puts them.
          They live outside the animated slide so the button just pressed is not
          unmounted under the keyboard. */}
      {multiple && (
        <div className="order-2 -ml-2 flex items-center gap-0.5">
          {banners.map((b, i) => (
            <button
              key={b.id}
              type="button"
              onClick={() => go(i)}
              aria-label={t('banners.goTo', { n: i + 1, total: count })}
              aria-current={i === active ? 'true' : undefined}
              className="grid h-6 place-items-center rounded-full px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
            >
              <span
                className={`block h-2 rounded-full transition-all duration-300 ${
                  i === active ? 'w-8 bg-[var(--accent-strong)]' : 'w-2 bg-white/35 hover:bg-white/60'
                }`}
              />
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? t('banners.resume') : t('banners.pause')}
            className="ml-1 grid h-6 w-6 place-items-center rounded-full text-white/75 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
          >
            {paused ? <Play className="size-3.5" aria-hidden /> : <Pause className="size-3.5" aria-hidden />}
          </button>
        </div>
      )}

      <div className="order-1">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current.id}
            role="group"
            aria-roledescription="slide"
            aria-label={t('banners.slideLabel', { n: active + 1, total: count })}
            drag={multiple ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.25}
            onDragStart={() => {
              dragged.current = true;
            }}
            onDragEnd={onDragEnd}
            // A swipe that ends on the link must not also follow it.
            onClickCapture={(e) => {
              if (!dragged.current) return;
              dragged.current = false;
              e.preventDefault();
            }}
            {...slideMotion}
          >
            {current.linkUrl ? (
              <a
                href={current.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mx-auto block w-fit max-w-full rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
              >
                {image}
              </a>
            ) : (
              image
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div
        aria-live={rotating ? 'off' : 'polite'}
        className="order-3"
      >
        <AnimatePresence mode="wait" initial={false}>
          {hasCopy && (
            <motion.div key={current.id} className="flex flex-col gap-2" {...slideMotion}>
              {/* A <p>, not a heading: globals.css sizes h1–h3 with unlayered rules
                  that beat Tailwind's layered utilities, and a promo line is not
                  document structure anyway (the page h1 lives in Hero). */}
              {current.title && (
                <p className="text-[40px] md:text-[56px] font-extrabold leading-none tracking-tight text-[var(--accent-strong)] break-words">
                  {current.title}
                </p>
              )}
              {current.subtitle && (
                <p className="text-[26px] md:text-[34px] font-extrabold leading-tight text-white break-words">
                  {current.subtitle}
                </p>
              )}
              {current.description && (
                <p className="mt-1 text-[15px] md:text-[17px] leading-relaxed text-white/85 break-words">
                  {current.description}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
